import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { visuallyHidden } from "@mui/utils";

import DOMPurify from "dompurify";

/* Custom imports */
import { useAppLoadingContext } from "../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../Context/ContextTypes/AppLoadingContextTypes";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

type FooterPropTypes = {
  footerSupportLinkItems: Array<{
    itemLinkText: string;
    itemLinkUrl: string;
    itemLinkTarget: string;
    itemLinkAriaLabel: string;
  }>;
  footerAboutLinkItems: Array<{
    itemLinkText: string;
    itemLinkUrl: string;
    itemLinkTarget: string;
    itemLinkAriaLabel: string;
  }>;
  footerLegalLinkItems: Array<{
    itemLinkText: string;
    itemLinkUrl: string;
    itemLinkTarget: string;
    itemLinkAriaLabel: string;
  }>;
  isInEditor: boolean;
  cqPath: string;
  cqType: string;
};

export default function FooterReact(props: FooterPropTypes) {
  const [firstAccordion, toggleFirstAccordion] = useState(false);
  const [secondAccordion, toggleSecondAccordion] = useState(false);
  const [thirdAccordion, toggleThirdAccordion] = useState(false);

  // translation
  const { language, translationsData } = useAppLoadingContext() as AppLoadingContextTypes;
  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    footerTitle: DomPurify("CTFS3-FOOTER-HEADER-TITLE"),
    supportTitle: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-004"),
    aboutTitle: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-008"),
    legalTitle: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-014"),
    downloadTitle: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-019"),
    downloadSubTitle: DomPurify("CTFS3-FOOTER-DOWNLOAD-SUB-TITLE"),
    footerImage: DomPurify("CTFS3-FOOTER-LOGO"),
    footerImageDescription: DomPurify("CTFS3-FOOTER-LOGO-ARIA-LABEL"),
    footerImageLink: DomPurify("CTFS3-FOOTER-LOGO-LINK"),
    footerImageLinkAriaLabel: DomPurify("CTFS3-FOOTER-LOGO-LINK-ARIA-LABEL"),
    footerRightReserved: DomPurify("CTFS3-FOOTER-RIGHT-RESERVED"),
    cdicLogo: DomPurify("CTFS3-FOOTER-CDIC-LOGO"),
    cdicLogoAlt: DomPurify("CTFS3-FOOTER-CDIC-LOGO-ARIA-LABEL"),
    cdicLogoLink: DomPurify("CTFS3-FOOTER-CDIC-LOGO-LINK"),
    cdicLogoAriaLabel: DomPurify("CTFS3-FOOTER-CDIC-LOGO-LINK-ARIA-LABEL"),
    cdicLink: DomPurify("CTFS3-FOOTER-CDIC-LINK"),
    cdicLinkAriaLabel: DomPurify("CTFS3-FOOTER-CDIC-LINK-ARIA-LABEL"),
    // downloadAppStoreImage: DomPurify("CTFS3-FOOTER-DOWNLOAD-APP-STORE"),
    downloadAppStoreImage: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-021"),
    // googlePlayImage: DomPurify("CTFS3-FOOTER-DOWNLOAD-GOOGLE-PLAY"),
    googlePlayImage: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-022"),
    essientialAccesibilityImage: DomPurify("CTFS3-FOOTER-ESSENTIAL-ACCESSIBILITY"),
    essientialAccesibilityText: DomPurify("CTFS3-PUB-NAV-FOOT-LBL-025"),
    essientialAccesibilityLink: DomPurify("CTFS3-FOOTER-ASSISTIVE-ACCESSIBILITY-LINK"),
    essientialAccesibilityAriaLabel: DomPurify("CTFS3-FOOTER-ASSISTIVE-ACCESSIBILITY-ARIA-LABEL"),
    essientialAccesibilityImageAlt: DomPurify("CTFS3-FOOTER-ASSISTIVE-ACCESSIBILITY-IMAGE-ALT"),
    essientialAccesibilityAnchorTitle: DomPurify("CTFS3-FOOTER-ASSISTIVE-ACCESSIBILITY-LINK-TITLE"),
    qrCodeUrl: DomPurify("CTFS3-FOOTER-QR-CODE"),
    qrCodeAriaLabel: DomPurify("CTFS3-FOOTER-QR-CODE-ARIA-LABEL"),
    appStoreLink: DomPurify("CTFS3-FOOTER-DOWNLOAD-APP-STORE-LINK"),
    appStoreAriaLabel: DomPurify("CTFS3-PUB-NAV-FOOT-APPLE-ARIAL"),
    appStoreImageAlt: DomPurify("CTFS3-PUB-NAV-FOOT-APPLE-IMAGE-ALT"),
    googlePlayLink: DomPurify("CTFS3-FOOTER-GOOGLE-PLAY-LINK"),
    googlePlayAriaLabel: DomPurify("CTFS3-PUB-NAV-FOOT-GOOGLE-ARIAL"),
    googlePlayImageAlt: DomPurify("CTFS3-PUB-NAV-FOOT-GOOGLE-IMAGE-ALT"),
    openNewWindow: DomPurify("CTFS3-PUB-AODA-OPENSNEWWINDOW"),
    cookieTitle: DomPurify("CTFS3-PUB-CTFLAN-092")
  };

  useEffect(() => {
    
      const legalFooterIds = document.querySelectorAll('[id^="legal_"]');

      legalFooterIds.forEach((legalLink) => { 
        let id: any = legalLink.getAttribute("id"); 
        let isClassPresent = document.querySelectorAll(`[class^="${id}"]`);
 
            if (isClassPresent.length) {
              let setTabindex = document.getElementById(id)?.getElementsByTagName('a')[0];
              setTabindex?.setAttribute('tabindex', '0');
              setTabindex?.setAttribute('aria-hidden', 'false');
            } else {
              let setTabindex = document.getElementById(id)?.getElementsByTagName('a')[0];
              setTabindex?.setAttribute('tabindex', '-1');
              setTabindex?.setAttribute('aria-hidden', 'true');
            }
      }
      );
  },[]);

  const otBtnStylesOverwrite = `
    .ot-sdk-show-settings {
        font-size: 14px !important;
        line-height: 14px !important;
        border: 0px !important;
        outline: 0px solid #fff !important;
        font-weight: normal;
        &:hover{
          font-weight: bold;
        }
        &:focus, &:focus-visible {
          outline: 0px solid #fff !important;
            &::before {
              outline: 0px solid #fff !important;
            }
        }
      }
    `;

  return (
    <Box
      component="footer"
      id="footerSection"
      className="content-section"
      padding={{ xs: "20px", sm: "20px", md: "20px", lg: "20px 60px" }}
      display="flex"
      justifyContent="center"
      sx={{
        backgroundColor: "#131313",
        color: "#ffffff",
        margin: "0px !important",
      }}
      role="contentinfo"
    >
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        width="100%"
        maxWidth={{ xs: "100%", sm: "100%", md: "100%", lg: "1296px" }}
      >
        {/* cdic logo row */}
        <Grid
          container
          direction="row"
          alignItems="center"
          textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={4}
            paddingRight="15px"
            textAlign={{ xs: "center", sm: "center", md: "center", lg: "right" }}
          >
            <Link
              href={translatedData.cdicLogoLink}
              target="_blank"
              rel="noreferrer"
              color="#fff"
              title={translatedData.openNewWindow}
              sx={{
                display: "inline-flex;",
                "&:focus": {
                  boxShadow: "0 0 0 5px hsl(0, 0%, 7%), 0 0 0 7px #007ffc, 0 0 0px 9px #fff;",
                  borderRadius: "2px",
                  fontWeight: "600",
                },
                "&:focus-visible": {
                  outline: "none",
                  outlineOffset: "0px;",
                },
              }}
            >
              <LazyLoadImage
                visibleByDefault
                effect="blur"
                delayTime={1000}
                width="125"
                height="57"
                src={translatedData.cdicLogo}
                alt={translatedData.cdicLogoAlt}
              />
            </Link>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8}>
            <Typography
              variant="body1"
              color="#fff !important"
              fontSize="18px !important"
              fontWeight="400 !important" 
              lineHeight="28px !important"
              textAlign={{xs:"center",lg:"left"}}
            
            >
              {translatedData.footerTitle}
            </Typography>
          </Grid>
        </Grid>

        <Divider
          aria-hidden="true"
          sx={{
            margin: "20px 0px",
            backgroundColor: "gray",
          }}
        />

        <Grid container direction={{ xs: "column", sm: "column", md: "column", lg: "row" }}>
          {/* support links container - desktop */}
          <Grid
            item
            lg={3}
            display={{ xs: "none", sm: "none", md: "none", lg: "flex" }}
            flexDirection="column"
          >
            <Typography
              variant="h2"
              fontWeight="700"
              fontSize="18px!important"
              color="#ffffff !important"
              textTransform="capitalize"
              textAlign="left"
            >
              {translatedData.supportTitle}
            </Typography>
            <ul
              id="supportFooterLinks"
              style={{
                listStyle: "none",
                paddingLeft: "0px",
                margin: "0px",
              }}
              role="list"
            >
              {props.footerSupportLinkItems?.map((link, idx) => (
                <li
                  role="listitem"
                  style={{
                    padding: "15px 0px",
                  }}
                  key={`footerCustomerSupport-${idx}`}
                >
                  <Link
                    id={`supportFooterLink${idx + 1}`}
                    fontSize="14px"
                    lineHeight="14px"
                    href={DomPurify(link.itemLinkUrl)}
                    target={link.itemLinkTarget}
                    color="#fff"
                    sx={{
                      boxShadow: "1.3em 1.3em 1.3em 1.3em #131313, 0 0 0 1.3em #131313",
                      textTransform: "capitalize",
                      margin: "15px 0px",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                        textUnderlinePosition: "under",
                        fontWeight: "600",
                      },

                      "&:focus": {
                        boxShadow:
                          "0 0 0 14px hsl(0, 0%, 7%), 0 0 0 16px #007ffc, 0 0 0px 18px #fff;",
                        borderRadius: "2px",
                        fontWeight: "600",
                      },
                      "&:focus-visible": {
                        outline: "2px solid #131313;",
                        outlineOffset: "0px;",
                      },
                    }}
                  >
                    {DomPurify(link.itemLinkText)}
                  </Link>
                </li>
              ))}
            </ul>
          </Grid>
          {/* support links accordion - tablet and below */}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            display={{ xs: "block", sm: "block", md: "block", lg: "none" }}
          >
            <Accordion sx={{ backgroundColor: "#131313", boxShadow: "none !important" }}>
              <AccordionSummary
                expandIcon={
                  firstAccordion ? (
                    <RemoveIcon sx={{ color: "#ffffff" }} />
                  ) : (
                    <AddIcon sx={{ color: "#ffffff" }} />
                  )
                }
                aria-controls="fcsPanel-content"
                id="fcs-panel"
                onClick={() => toggleFirstAccordion(!firstAccordion)}
                sx={{
                  "&:focus": {
                    outline: "2px solid white;",
                    borderRadius: "3px;",
                    border: "2px solid #007ffc;",
                  },
                }}
              >
                <Typography
                  variant="h2"
                  color="#ffffff !important"
                  fontWeight="bold"
                  fontSize="18px!important"
                  textTransform="capitalize"
                  textAlign="left"
                >
                  {translatedData.supportTitle}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
                <List
                  role="list"
                  id="supportFooterLinksMobile"
                  sx={{
                    listStyle: "none",
                    paddingLeft: "0px",
                    margin: "0px",
                  }}
                >
                  {props.footerSupportLinkItems?.map((link, idx) => (
                    <ListItem
                      role="listitem"
                      sx={{
                        padding: "15px 0px",
                      }}
                      key={`fcsPanelLink-${idx}`}
                    >
                      <Link
                        href={DomPurify(link.itemLinkUrl)}
                        fontSize="14px"
                        lineHeight="14px"
                        target={link.itemLinkTarget}
                        color="#fff"
                        sx={{
                          textTransform: "capitalize",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                            textUnderlinePosition: "under",
                            fontWeight: "600",
                          },
                          "&:focus": {
                            boxShadow:
                              "0 0 0 11px hsl(0, 0%, 7%), 0 0 0 13px #007ffc, 0 0 0px 15px #fff;",
                            borderRadius: "2px",
                            fontWeight: "600",
                          },
                          "&:focus-visible": {
                            outline: "2px solid #131313;",
                            outlineOffset: "0px;",
                          },
                        }}
                      >
                        {DomPurify(link.itemLinkText) || `Customer support ${idx}`}
                      </Link>
                    </ListItem>
                  ))}  
                </List>
              </AccordionDetails>
            </Accordion>

            <Divider
              aria-hidden="true"
              sx={{
                margin: "20px 0px",
                backgroundColor: "gray",
              }}
            />
          </Grid>

          {/* about ctb links - desktop */}
          <Grid
            item
            lg={3}
            display={{ xs: "none", sm: "none", md: "none", lg: "flex" }}
            flexDirection="column"
          >
            <Typography
              variant="h2"
              fontWeight="700"
              fontSize="18px!important"
              color="#ffffff !important"
              textTransform="capitalize"
              textAlign="left"
            >
              {translatedData.aboutTitle}
            </Typography>
            <ul
              role="list"
              style={{
                listStyle: "none",
                paddingLeft: "0px",
                margin: "0px",
              }}
            >
              {props.footerAboutLinkItems?.map((link, idx) => (
                <li
                  role="listitem"
                  style={{
                    padding: "15px 0px",
                  }}
                  key={`footerAboutCTBLink-${idx}`}
                >
                  <Link
                    id={`aboutCTBFooterLink${idx + 1}`}
                    fontSize="14px"
                    lineHeight="14px"
                    href={DomPurify(link.itemLinkUrl)}
                    target={link.itemLinkTarget}
                    title={link.itemLinkTarget === "_blank" ? translatedData.openNewWindow : undefined}
                    color="#fff"
                    sx={{
                      boxShadow: "1.3em 1.3em 1.3em 1.3em #131313, 0 0 0 1.3em #131313",
                      textTransform: "capitalize",
                      margin: "15px 0px",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                        textUnderlinePosition: "under",
                        fontWeight: "600",
                      },

                      "&:focus": {
                        boxShadow:
                          "0 0 0 14px hsl(0, 0%, 7%), 0 0 0 16px #007ffc, 0 0 0px 18px #fff;",
                        borderRadius: "2px",
                        fontWeight: "600",
                      },
                      "&:focus-visible": {
                        outline: "2px solid #131313;",
                        outlineOffset: "0px;",
                      },
                    }}
                  >
                    {DomPurify(link.itemLinkText)}
                  </Link>
                </li>
              ))}
            </ul>
          </Grid>
          {/* about ctb accordion links - tablet and lower */}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            display={{ xs: "block", sm: "block", md: "block", lg: "none" }}
          >
            <Accordion sx={{ backgroundColor: "#131313", boxShadow: "none !important" }}>
              <AccordionSummary
                expandIcon={
                  secondAccordion ? (
                    <RemoveIcon sx={{ color: "#ffffff" }} />
                  ) : (
                    <AddIcon sx={{ color: "#ffffff" }} />
                  )
                }
                aria-controls="factb-content"
                id="factb-panel"
                onClick={() => toggleSecondAccordion(!secondAccordion)}
                sx={{
                  "&:focus": {
                    outline: "2px solid white;",
                    borderRadius: "3px;",
                    border: "2px solid #007ffc;",
                  },
                }}
              >
                <Typography
                  variant="h2"
                  color="#ffffff !important"
                  fontSize="18px!important"
                  fontWeight="bold"
                  textTransform="capitalize"
                  textAlign="left"
                >
                  {translatedData.aboutTitle}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
                <List
                  role="list"
                  id="aboutUsFooterLinks"
                  sx={{
                    listStyle: "none",
                    paddingLeft: "0px",
                    margin: "0px",
                  }}
                >
                  {props.footerAboutLinkItems?.map((link, idx) => (
                    <ListItem
                      role="listitem"
                      sx={{
                        padding: "15px 0px",
                      }}
                      key={`fcsPanelLink-${idx}`}
                    >
                      <Link
                        key={`factbPanelLink-${idx}`}
                        href={DomPurify(link.itemLinkUrl)}
                        fontSize="14px"
                        lineHeight="14px"
                        target={link.itemLinkTarget}
                        title={link.itemLinkTarget === "_blank" ? translatedData.openNewWindow : undefined}
                        color="#fff"
                        sx={{
                          textTransform: "capitalize",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                            textUnderlinePosition: "under",
                            fontWeight: "600",
                          },
                          "&:focus": {
                            boxShadow:
                              "0 0 0 11px hsl(0, 0%, 7%), 0 0 0 13px #007ffc, 0 0 0px 15px #fff;",
                            borderRadius: "2px",
                            fontWeight: "600",
                          },
                          "&:focus-visible": {
                            outline: "none;",
                            outlineOffset: "0px;",
                          },
                        }}
                      >
                        {DomPurify(link.itemLinkText)}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <Divider
              aria-hidden="true"
              sx={{
                margin: "20px 0px",
                backgroundColor: "gray",
              }}
            />
          </Grid>

          {/* legal links - desktop */}
          <Grid
            item
            lg={3}
            display={{ xs: "none", sm: "none", md: "none", lg: "flex" }}
            flexDirection="column"
          >
            <Typography
              variant="h2"
              fontSize="18px!important"
              fontWeight="700"
              color="#ffffff !important"
              textTransform="capitalize"
              textAlign="left"
            >
              {translatedData.legalTitle}
            </Typography>
            <ul
              id="legalFooterLinks"
              role="list"
              style={{
                listStyle: "none",
                paddingLeft: "0px",
                margin: "0px",
              }}
            >
              {props.footerLegalLinkItems?.map((link, idx) => (
                <li
                  role="listitem"
                  style={{
                    padding: "15px 0px",
                  }}
                  key={`footerLegalLink-${idx}`}
                >
                  <Link
                    id={`legalFooterLink${idx + 1}`}
                    fontSize="14px"
                    lineHeight="14px"
                    href={DomPurify(link.itemLinkUrl)}
                    color="#fff"
                    sx={{
                      boxShadow: "1.3em 1.3em 1.3em 1.3em #131313, 0 0 0 1.3em #131313",
                      textTransform: "capitalize",
                      margin: "15px 0px",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                        textUnderlinePosition: "under",
                        fontWeight: "600",
                      },
                      "&:focus": {
                        boxShadow:
                          "0 0 0 14px hsl(0, 0%, 7%), 0 0 0 16px #007ffc, 0 0 0px 18px #fff;",
                        borderRadius: "2px",
                        fontWeight: "600",
                      },
                      "&:focus-visible": {
                        outline: "none;",
                        outlineOffset: "0px;",
                      },
                    }}
                  >
                    {DomPurify(link.itemLinkText) || `Legal support ${idx}`}
                  </Link>
                </li>
              ))}
              
              {/* OneTrust cookie settings link */}
              <li className="MuiListItem-root MuiListItem-gutters MuiListItem-padding css-1dxtn5q" role="listitem" 
              style={{
                padding: "15px 0px",
                marginTop:"2px"
                }}>
                <style>{otBtnStylesOverwrite}</style>
                <a 
                  className="ot-sdk-show-settings css-imndwg MuiTypography-root MuiTypography-inherit MuiLink-root MuiLink-underlineAlways css-118v7ss" 
                  data-hide-in-browse-only-mode="false" 
                  data-link-value="{translatedData.cookieTitle}" 
                  data-feature-type="footer" 
                  id="ot-sdk-btn"
                  href="#">
                    {translatedData.cookieTitle}
                </a>
              </li>
              
            </ul>
          </Grid>
          {/* legal accordion links - tablet and lower */}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            display={{ xs: "block", sm: "block", md: "block", lg: "none" }}
          >
            <Accordion sx={{ backgroundColor: "#131313", boxShadow: "none !important" }}>
              <AccordionSummary
                expandIcon={
                  thirdAccordion ? (
                    <RemoveIcon sx={{ color: "#ffffff" }} />
                  ) : (
                    <AddIcon sx={{ color: "#ffffff" }} />
                  )
                }
                aria-controls="fl-content"
                id="fl-panel"
                onClick={() => toggleThirdAccordion(!thirdAccordion)}
                sx={{
                  "&:focus": {
                    outline: "2px solid white;",
                    borderRadius: "3px;",
                    border: "2px solid #007ffc;",
                  },
                }}
              >
                <Typography
                  variant="h2"
                  fontSize="18px!important"
                  color="#ffffff !important"
                  fontWeight="bold"
                  textTransform="capitalize"
                  textAlign="left"
                >
                  {translatedData.legalTitle}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
                <List
                  role="list"
                  id="legalFooterLinksMobile"
                  sx={{
                    listStyle: "none",
                    paddingLeft: "0px",
                    margin: "0px",
                  }}
                >
                  {props.footerLegalLinkItems?.map((link, idx) => (
                    <ListItem
                      role="listitem"
                      sx={{
                        padding: "15px 0px",
                      }}
                      key={`fcsPanelLink-${idx}`}
                    >
                      <Link
                        key={`flPanelLink-${idx}`}
                        href={DomPurify(link.itemLinkUrl)}
                        fontSize="14px"
                        lineHeight="14px"
                        color="#fff"
                        sx={{
                          textTransform: "capitalize",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                            textUnderlinePosition: "under",
                            fontWeight: "600",
                          },
                          "&:focus": {
                            boxShadow:
                              "0 0 0 11px hsl(0, 0%, 7%), 0 0 0 13px #007ffc, 0 0 0px 15px #fff;",
                            borderRadius: "2px",
                            fontWeight: "600",
                          },
                          "&:focus-visible": {
                            outline: "none;",
                            outlineOffset: "0px;",
                          },
                        }}
                      >
                        {DomPurify(link.itemLinkText)}
                      </Link>
                    </ListItem>
                  ))}
                  
                  {/* OneTrust cookie settings link */}
                  <li className="MuiListItem-root MuiListItem-gutters MuiListItem-padding css-1dxtn5q" role="listitem" 
                  style={{
                    padding: "15px 0px",
                    marginTop:"2px"
                    }}>
                    <style>{otBtnStylesOverwrite}</style>
                    <a 
                      className="ot-sdk-show-settings css-imndwg MuiTypography-root MuiTypography-inherit MuiLink-root MuiLink-underlineAlways css-118v7ss" 
                      data-hide-in-browse-only-mode="false" 
                      data-link-value="{translatedData.cookieTitle}" 
                      data-feature-type="footer" 
                      id="ot-sdk-btn"
                      href="#">
                        {translatedData.cookieTitle}
                    </a>
                  </li>
                  
                </List>
              </AccordionDetails>
            </Accordion>
            <Divider
              aria-hidden="true"
              sx={{
                margin: "20px 0px",
                backgroundColor: "gray",
              }}
            />
          </Grid>

          {/* download the app section */}
          <Grid
            id="downloadAppFooterContainer"
            item
            lg={3}
            display="flex"
            flexDirection="column"
            alignItems={{ xs: "center", sm: "center", md: "center", lg: "initial" }}
          >
            <Typography
              variant="h2"
              fontSize="18px !important"
              lineHeight="14px!important"
              fontWeight="700"
              color="#ffffff !important"
              alignSelf={{ md: "center", lg: "flex-start" }}
            >
              {translatedData.downloadTitle}
            </Typography>

            <Typography
              variant="body1"
              fontSize="14px"
              lineHeight="14px"
              color="#ffffff !important"
              sx={{ margin: "10px 0px" }}
              display={{ xs: "none", sm: "none", md: "none", lg: "block" }}
            >
              {translatedData.downloadSubTitle}
            </Typography>

            <Box
              sx={{
                borderRadius: "10px",
                backgroundColor: "#fff",
                color: "#000",
                width: "140px",
                height: "125px",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "30px",
              }}
              display={{ xs: "none", sm: "none", md: "none", lg: "block" }}
            >
              <LazyLoadImage
                visibleByDefault
                effect="blur"
                delayTime={1000}
                src={translatedData.qrCodeUrl}
                alt={translatedData.qrCodeAriaLabel}
                width="140px"
                height="140px"
              />
            </Box>

            <Grid
              item
              display="flex"
              flexDirection={{ xs: "row", sm: "row", md: "row", lg: "column" }}
              alignItems={{ xs: "center", sm: "center", md: "center", lg: "initial" }}
              component="ul"
              sx={{
                margin: "0px",
                padding: "0px",
                listStyle: "none",
              }}
              role="list"
            >
              <Grid
                item
                component="li"
                role="listitem"
                marginRight={{ xs: "5px", sm: "5px", md: "5px", lg: "0px" }}
              >
                <Link
                  id="apple-store-link"
                  href={translatedData.appStoreLink}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    marginRight: "10px",
                    display: "inline-flex;",
                    "&:focus": {
                      boxShadow: "0 0 0 5px hsl(0, 0%, 7%), 0 0 0 7px #007ffc, 0 0 0px 9px #fff;",
                      borderRadius: "2px",
                      fontWeight: "600",
                    },
                    "&:focus-visible": {
                      outline: "none",
                      outlineOffset: "0px;",
                    },
                  }}
                >
                  <LazyLoadImage
                    visibleByDefault
                    effect="blur"
                    delayTime={1000}
                    src={translatedData.downloadAppStoreImage}
                    style={{ height: "50.5px" }}
                    // height="50"
                    // width="140"
                    alt={translatedData.appStoreAriaLabel}
                  />
                </Link>
              </Grid>

              <Grid
                item
                component="li"
                role="listitem"
                marginLeft={{ xs: "5px", sm: "5px", md: "5px", lg: "0px" }}
                width={{ lg: "156px" }}
                height={{ lg: "80px" }}
              >
                <Link
                  id="google-play-store-link"
                  href={translatedData.googlePlayLink}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    display: "inline-flex;",
                    marginLeft: "-14px",
                    "&:focus": {
                      boxShadow: "0 0 0 0px hsl(0, 0%, 7%), 0 0 0 2px #007ffc, 0 0 0px 4px #fff;",
                      borderRadius: "2px",
                      fontWeight: "600",
                    },
                    "&:focus-visible": {
                      outline: "none",
                      outlineOffset: "0px;",
                    },
                  }}
                >
                  <LazyLoadImage
                    visibleByDefault
                    effect="blur"
                    delayTime={1000}
                    src={translatedData.googlePlayImage}
                    style={{ height: "70px", marginLeft: "4px" }}
                    // height="80"
                    // width="180"
                    alt={translatedData.googlePlayAriaLabel}
                  />
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* essential accessibility row */}
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent={{ xs: "center", sm: "center", md: "center", lg: "initial" }}
        >
          <Grid item sx={{ color: "#fff" }}>
            <span>{translatedData.essientialAccesibilityText}</span>
            <Typography
              variant="caption"
              aria-hidden="true"
              sx={visuallyHidden}
              id="footer-levelAccessAriaText"
            >
              {translatedData.essientialAccesibilityAriaLabel}
            </Typography>
          </Grid>
          <Grid item>
            <Link
              href={translatedData.essientialAccesibilityLink}
              target="_blank"
              rel="noreferrer"
              aria-labelledby="footer-levelAccessAriaText"
              title={translatedData.essientialAccesibilityAnchorTitle}
              sx={{
                marginLeft: "10px",
                display: "inline-flex;",
                "&:focus": {
                  boxShadow: "0 0 0 5px hsl(0, 0%, 7%), 0 0 0 7px #007ffc, 0 0 0px 9px #fff;",
                  borderRadius: "2px",
                  fontWeight: "600",
                },
                "&:focus-visible": {
                  outline: "none",
                  outlineOffset: "0px;",
                },
              }}
            >
              <LazyLoadImage
                visibleByDefault
                effect="blur"
                delayTime={1000}
                src={translatedData.essientialAccesibilityImage}
                width="100px"
                height="41px"
                alt={translatedData.essientialAccesibilityAriaLabel}
                style={{}}
              />
            </Link>
          </Grid>
        </Grid>
        <Divider
          aria-hidden="true"
          sx={{
            margin: "20px 0px",
            backgroundColor: "gray",
          }}
        />

        {/* CTB logo row */}
        <Grid
          container
          direction={{ xs: "column", sm: "column", md: "column", lg: "row" }}
          alignItems="center"
          margin="20px 0px"
          display="flex"
        >
          <Grid item xs={12} sm={12} md={12} lg={1.1}>
            <Link
              href={translatedData.footerImageLink}
              rel="noreferrer"
              color="#fff"
              sx={{
                marginLeft: "10px",
                display: "inline-flex;",
                "&:focus": {
                  boxShadow: "0 0 0 5px hsl(0, 0%, 7%), 0 0 0 7px #007ffc, 0 0 0px 9px #fff;",
                  borderRadius: "2px",
                  fontWeight: "600",
                },
                "&:focus-visible": {
                  outline: "none",
                  outlineOffset: "0px;",
                },
              }}
            >
              <LazyLoadImage
                visibleByDefault
                effect="blur"
                delayTime={1000}
                width="100"
                height="41"
                style={{}}
                src={translatedData.footerImage}
                alt={translatedData.footerImageLinkAriaLabel}
              />
            </Link>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={10.9}>
            <Typography
              variant="body1"
              color="#ffffff !important"
              fontSize="14px"
              lineHeight="14px"
              textTransform="capitalize"
              marginLeft="15px"
            >
              {translatedData.footerRightReserved}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
